import { ReadonlyVec2 } from 'gl-matrix';
import { PanZoom } from '../../view/PanZoom';
import { PlaneView } from '../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';

enum ZoomActionFocus {
    VIEW_CENTER,
    CURSOR,
}

/**
 * {@link ZoomAction} provides functionality to modify the {@link PanZoom.scale scaling} of a {@link PanZoom}-object.
 */
export class ZoomAction extends ToolAction {
    /**
     * The {@link PanZoom}-object that this {@link ZoomAction} will modify.
     */
    private readonly panZoom: PanZoom;

    /**
     * {@link ModifierKeys} that need to be active to use this {@link ZoomAction}.
     */
    private readonly modifierKeys: ModifierKeys;

    /**
     * {@link ZoomActionFocus} enum used to determine the point to which the zoom events should focus.
     */
    private static FocusMode: ZoomActionFocus = ZoomActionFocus.CURSOR;

    /**
     * {@link ZoomAction} constructor, which takes the {@link PanZoom}-object and optional {@link ModifierKeys} as parameters.
     * @param panZoom The {@link PanZoom}-object that is to be altered by this {@link ZoomAction}.
     * @param modifierKeys {@link ModifierKeys} that need to be active to use this {@link ZoomAction}. Defaults to {@link ModifierKeys.NONE NONE}.
     */
    public constructor(
        panZoom: PanZoom,
        modifierKeys: ModifierKeys = ModifierKeys.NONE
    ) {
        super();
        this.panZoom = panZoom;
        this.modifierKeys = modifierKeys;
    }

    /**
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ZoomAction}.\
     * This updates the {@link PanZoom.scale scaling} of the {@link panZoom}-object if the event is successfully handled.
     * @param delta The direction of the MouseWheel event.\
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if the event was successfully handled.
     * The event is always successfully handled if the {@link modifierKeys} match.\
     * {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} if the {@link modifierKeys} don't match, in which case the event wasn't handled.
     */
    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (modifierKeys.equals(this.modifierKeys)) {
            if (delta > 0) {
                this.panZoom.zoomIn(
                    view,
                    ZoomAction.FocusMode == ZoomActionFocus.CURSOR
                        ? coordinates
                        : undefined
                );
            } else {
                this.panZoom.zoomOut(
                    view,
                    ZoomAction.FocusMode == ZoomActionFocus.CURSOR
                        ? coordinates
                        : undefined
                );
            }

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
